// StructureData envelope: use (selector) first, auto (generated), manual (override).
export type StructureDataEnvelope = {
  use: "auto" | "manual";
  auto: Record<string, any>;
  manual: Record<string, any> | null;
};

export const isStructureDataEnvelope = (value: any): value is StructureDataEnvelope =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (value.use === "auto" || value.use === "manual");

// Legacy plain JSON-LD (no `use` key) wraps as the envelope's auto section.
export const normalizeStructureData = (raw: unknown): StructureDataEnvelope => {
  let parsed: any = raw;
  if (typeof raw === "string") {
    try {
      parsed = raw === "" ? {} : JSON.parse(raw);
    } catch {
      parsed = {};
    }
  }
  if (isStructureDataEnvelope(parsed)) {
    return {
      use: parsed.use,
      auto: parsed.auto && typeof parsed.auto === "object" ? parsed.auto : {},
      manual: parsed.manual && typeof parsed.manual === "object" ? parsed.manual : null,
    };
  }
  const auto = parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  return { use: "auto", auto, manual: null };
};

// Active section for display/render; manual missing while selected falls back to auto.
export const resolveStructureData = (envelope: StructureDataEnvelope): Record<string, any> =>
  envelope.use === "manual" && envelope.manual && Object.keys(envelope.manual).length !== 0
    ? envelope.manual
    : envelope.auto;

// Regeneration entry point: replaces auto while preserving a stored manual/use.
export const mergeAutoStructureData = (storedRaw: unknown, auto: Record<string, any>): string =>
  JSON.stringify({ ...normalizeStructureData(storedRaw), auto });

// Like mergeAutoStructureData, but preserves LEGACY hand-edited markup: when the stored value is a
// plain (non-envelope) object that differs from the freshly generated `auto`, it was hand-authored
// through the old free editor, so keep it as a `manual` override (use: "manual") rather than losing
// it. Values already in envelope form, or matching the generated shape, migrate to use: "auto".
export const migrateAutoStructureData = (storedRaw: unknown, auto: Record<string, any>): string => {
  const wasEnvelope = isStructureDataEnvelope(
    typeof storedRaw === "string" && storedRaw !== "" ? safeParse(storedRaw) : storedRaw,
  );
  const normalized = normalizeStructureData(storedRaw);
  const legacyHandEdited =
    !wasEnvelope &&
    Object.keys(normalized.auto).length !== 0 &&
    JSON.stringify(normalized.auto) !== JSON.stringify(auto);
  return JSON.stringify(
    legacyHandEdited ? { use: "manual", auto, manual: normalized.auto } : { ...normalized, auto },
  );
};

const safeParse = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};
